import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import parsePhoneNumberFromString from "libphonenumber-js";
import { z } from "zod";
import {
  differenceInCalendarDays,
  format,
  isSameDay,
  isValid,
  parse,
  startOfDay,
} from "date-fns";
import { Badge } from "@/components/ui/badge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const SCROLLBAR_CLASS =
  "[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent";

// Maximum allowed image upload size (5 MB), shared across all image upload inputs.
export const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

// Money-style number inputs (prices, tips, adjustments, stock values) are
// capped at four digits, so a mistyped value cannot turn into an absurd total.
export const MAX_AMOUNT = 9999;

// The same cap for inputs that also accept negative values.
export const MIN_NEGATIVE_AMOUNT = -MAX_AMOUNT;

export const truncate = (str, length) => {
  if (!str || str.length <= length) return str ?? null;
  return `${str.slice(0, length - 3)}...`;
};

// total mins convert in to actual time
export const formatMinutes = (totalMinutes) => {
  if (!totalMinutes) return "0 min";

  if (typeof totalMinutes === "string") {
    return totalMinutes.trim() ? totalMinutes : "0 min";
  }
  // if (!totalMinutes || totalMinutes <= 0) return "0 min";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours && minutes)
    return `${hours} hour${hours > 1 ? "s" : ""} ${minutes} min`;
  if (hours) return `${hours} hour${hours > 1 ? "s" : ""}`;
  return `${minutes} min`;
};

/**
 * A payment mode as stored ("gift_card") read as words ("Gift Card"), so a raw
 * database value never reaches the screen.
 */
export const formatPaymentMode = (mode) => {
  if (!mode) return "-";

  return String(mode)
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export const formatCurrency = (amount = 0, options = {}) => {
  const { showDecimals = true } = options;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: 2,
    trailingZeroDisplay: showDecimals ? "auto" : "stripIfInteger",
  }).format(amount);
};

export function formatDisplayPrice(price, priceType) {
  if (price == null) return null;

  const formatted = formatCurrency(price);

  if (!priceType) return formatted;

  switch (priceType) {
    case "starting_at":
      return `From ${formatted}`;
    case "fixed":
      return formatted;
    default:
      return formatted;
  }
}

export const getTaxLabel = (data) => {
  const paidTaxAmount = Number(data?.paid_tax_amount || 0);

  if (paidTaxAmount <= 0) {
    return "Tax";
  }

  return `Tax (Paid ${formatCurrency(paidTaxAmount)})`;
};

export const getFullName = (firstName, lastName) => {
  return [firstName, lastName].filter(Boolean).join(" ");
};

/**
 * Forms that ask for one full name still save first and last names: the first
 * word is the first name and the rest, if any, the last name.
 */
export const splitFullName = (fullName = "") => {
  const [firstName = "", ...rest] = String(fullName).trim().split(/\s+/);
  return { first_name: firstName, last_name: rest.join(" ") };
};

export const getStaffName = (user) =>
  user?.nick_name?.trim() || getFullName(user?.first_name, user?.last_name);

export const getClientName = (client) =>
  getFullName(client?.first_name, client?.last_name);

// A walk-in appointment or sale carries no client record. Every surface that
// would otherwise print an empty name shows this label instead.
export const WALK_IN_LABEL = "Walk-In";

export const getClientDisplayName = (client) =>
  getClientName(client) || WALK_IN_LABEL;

export const getInitials = (firstName = "", lastName = "") => {
  const first = firstName?.trim()?.charAt(0) || "";
  const last = lastName?.trim()?.charAt(0) || "";

  return `${first}${last}`.toUpperCase();
};

// calendar for ------------------------- ====== --------------------------------------------------
export const getWeekDays = (currentDate) => {
  const day = new Date(currentDate);
  const dayOfWeek = day.getDay();

  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const startOfWeek = new Date(day);
  startOfWeek.setDate(day.getDate() + diff);

  const weekDays = [];

  for (let i = 0; i < 7; i++) {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    weekDays.push(`${year}-${month}-${day}`);
  }

  return weekDays;
};

export const getMonthDays = (currentDate) => {
  const date = new Date(currentDate);

  // Get first and last day of the month
  const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);

  const daysArray = [];

  // Get ISO weekday (Mon = 1, Sun = 7)
  const getIsoWeekday = (d) => (d.getDay() === 0 ? 7 : d.getDay());

  // Fill days before start of the month (from previous month)
  const startDay = getIsoWeekday(startOfMonth);
  for (let i = startDay - 1; i > 0; i--) {
    const prevDate = new Date(startOfMonth);
    prevDate.setDate(startOfMonth.getDate() - i);
    daysArray.push(format(prevDate, "yyyy-MM-dd"));
  }

  // Fill all days of current month
  for (let i = 1; i <= endOfMonth.getDate(); i++) {
    const day = new Date(date.getFullYear(), date.getMonth(), i);
    daysArray.push(format(day, "yyyy-MM-dd"));
  }

  // Fill days after the end of the month (from next month)
  const endDay = getIsoWeekday(endOfMonth);
  for (let i = endDay + 1; i <= 7; i++) {
    const nextDate = new Date(endOfMonth);
    nextDate.setDate(endOfMonth.getDate() + (i - endDay));
    daysArray.push(format(nextDate, "yyyy-MM-dd"));
  }

  return daysArray;
};

export const getDayHours = (currentDate) => {
  const date = new Date(currentDate);
  const startOfDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const dayHours = [];

  for (let i = 0; i < 24; i++) {
    const hourDate = new Date(startOfDay);
    hourDate.setHours(i);
    const hour = hourDate.getHours().toString().padStart(2, "0"); // Format to "HH"
    dayHours.push(hour);
  }

  return dayHours;
};

export const getFilterDate = (dateString) => {
  const dateObject = parseLocalDate(dateString);
  if (!dateObject) return "";
  return dateObject.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const isOrderCompleted = (status) => {
  return [
    "completed",
    "scheduled_invoicing",
    "pending_invoicing",
    "interim_completed",
  ].includes(status);
};

export function filterCustomerName(client) {
  return [client?.first_name, client?.last_name].filter(Boolean).join(" ");
}

export function capitalizeFLetter(string) {
  if (!string) return "";
  return string[0].toUpperCase() + string.slice(1);
}

export const getGoogleAddressComponent = (
  components,
  type,
  useShortName = false,
) => {
  const match = components.find((comp) => comp.types.includes(type));
  if (!match) return "";
  return (useShortName ? match.short_name : match.long_name) || "";
};

// Returns a GMT offset label (e.g. "GMT-5", "GMT+5:30") for an IANA timezone.
// Falls back to the browser's offset when no timezone is provided.
export const formatGmtOffset = (timezone) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone || undefined,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    const offset = parts.find((part) => part.type === "timeZoneName")?.value;
    if (!offset) return "";
    // Intl returns "GMT" for UTC; normalize to "GMT+0" to match date-fns "O".
    return offset === "GMT" ? "GMT+0" : offset;
  } catch {
    return format(new Date(), "O");
  }
};

// Location wall-clock helpers. Appointment dates/times from the API are the
// location's wall-clock values and are handled as browser-local Dates, so
// "now"/"today" and UTC timestamps (created_at, payment_date…) are read on the
// location's clock too. Without a (valid) timezone the browser clock is used.
export const toTimezoneDate = (value = new Date(), timezone) => {
  const date = new Date(value);
  if (!timezone) return date;

  try {
    return new Date(date.toLocaleString("en-US", { timeZone: timezone }));
  } catch {
    return date;
  }
};

export const getNowInTimezone = (timezone) => toTimezoneDate(new Date(), timezone);

// Super admin screens show every time in Arizona time (MST, no DST).
export const ADMIN_TIMEZONE = "America/Phoenix";
export const ADMIN_TIMEZONE_LABEL = "MST";

// API timestamps. "2026-10-21 02:19:55" (Laravel toDateTimeString) has no
// zone but is UTC, so read it as UTC instead of browser-local time.
export const parseUtcTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;

  const text = String(value).trim();
  const date = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(text)
    ? new Date(`${text.replace(" ", "T")}Z`)
    : new Date(text);

  return isValid(date) ? date : null;
};

// Formats an API timestamp on a timezone's clock with a date-fns pattern.
export const formatInTimezone = (
  value,
  timezone,
  pattern = "MMM d, yyyy, h:mm a",
  fallback = "-",
) => {
  const date = parseUtcTimestamp(value);
  if (!date) return value ? String(value) : fallback;

  return format(toTimezoneDate(date, timezone), pattern);
};

export const formatAdminDateTime = (value, pattern, fallback) =>
  formatInTimezone(value, ADMIN_TIMEZONE, pattern, fallback);

export const getTodayForTimezone = (timezone) =>
  startOfDay(getNowInTimezone(timezone));

// Timezone of the selected location; "All locations" uses the business' first
// location, as the API does.
export const getLocationTimezone = (locations, locationId) => {
  const withTimezone = (locations || []).filter((location) => location?.timezone);
  const selected = withTimezone.find(
    (location) => String(location.id) === String(locationId),
  );
  // The API's business zone comes from its first open location.
  const first = withTimezone
    .filter((location) => !location.is_deleted)
    .sort((a, b) => a.id - b.id)[0];

  return (selected ?? first)?.timezone || null;
};

// 11:00:00 convert to 03:40 PM
export const formatTime = (time) => {
  if (!time) return "";

  return new Date(`1970-01-01T${time}`).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true, // change to false if you want 24h
  });
};

// My-Date-Picker for
export const getDefaultPresets = (timezone) => [
  { label: "Today", day: 0 },
  { label: "Yesterday", day: 1 },
  { label: "Last 7 Days", day: 7 },
  { label: "Current Month", day: getNowInTimezone(timezone).getDate() - 1 },
  { label: "Last 30 Days", day: 30 },
  { label: "Last 3 Months", day: 90 },
  { label: "Last 12 Months", day: 365 },
];

const normalizeCalendarDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (typeof value === "string") {
    const dayMonthYearDate = parse(value, "dd-MM-yyyy", new Date());
    if (isValid(dayMonthYearDate)) return dayMonthYearDate;

    const isoDate = parseLocalDate(value);
    if (isoDate) return isoDate;
  }

  return parseLocalDate(value);
};

// Show the "Today" shortcut on end-date fields only when the selected
// start date is actually today. We normalize supported date formats first
// so all calendar sidebars use the same comparison rule.
export const shouldShowTodayForEndDate = (startDate, timezone) => {
  const normalizedStartDate = normalizeCalendarDate(startDate);

  if (!normalizedStartDate) return false;

  return isSameDay(startOfDay(normalizedStartDate), getTodayForTimezone(timezone));
};

export const getEndDateMinDate = (startDate) => {
  const normalizedStartDate = normalizeCalendarDate(startDate);

  return normalizedStartDate ? startOfDay(normalizedStartDate) : undefined;
};

export const isEndDateBeforeStartDate = (startDate, endDate) => {
  const normalizedStartDate = normalizeCalendarDate(startDate);
  const normalizedEndDate = normalizeCalendarDate(endDate);

  if (!normalizedStartDate || !normalizedEndDate) return false;

  return (
    startOfDay(normalizedEndDate).getTime() <
    startOfDay(normalizedStartDate).getTime()
  );
};

export const isSameCalendarDate = (startDate, endDate) => {
  const normalizedStartDate = normalizeCalendarDate(startDate);
  const normalizedEndDate = normalizeCalendarDate(endDate);

  if (!normalizedStartDate || !normalizedEndDate) return false;

  return isSameDay(
    startOfDay(normalizedStartDate),
    startOfDay(normalizedEndDate),
  );
};

export const waitlistDaysOptions = [
  { label: "1 day", value: "1" },
  { label: "3 days", value: "3" },
  { label: "7 days", value: "7" },
  { label: "14 days", value: "14" },
  { label: "30 days", value: "30" },
  { label: "90 days", value: "90" },
];

export const usPhoneSchema = z
  .string()
  .min(10, "Enter a valid US phone number")
  .refine((value) => {
    if (value.length !== 10) return false;

    const phone = parsePhoneNumberFromString(value, "US");

    return phone?.isValid();
  }, "Enter a valid US phone number");

/** Optional phone: empty is allowed, anything typed must be a valid US number. */
export const optionalUsPhoneSchema = z
  .union([z.literal(""), usPhoneSchema])
  .nullable()
  .optional();

export const formatPhoneNumber = (phone) => {
  if (!phone) return "";

  const cleaned = phone.replace(/\D/g, "").slice(0, 10);

  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (!match) return phone;

  return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
};

/**
 * Email on a client added from the business panel: optional, checked only
 * when something is typed. Online booking and the client hub still require it.
 */
export const optionalEmailSchema = z
  .string()
  .trim()
  .refine(
    (value) => !value || z.string().email().safeParse(value).success,
    "Please enter a valid email address",
  )
  .nullable()
  .optional();

/** A client's contact line for lists and pickers: email, else phone. */
export const clientContactLabel = (client) =>
  client?.email || (client?.phone ? formatPhoneNumber(client.phone) : "");

/** "2026-03-07" → "Mar 7, 2026"  |  already readable → passthrough */
export function formatDate(raw) {
  if (!raw) return "—";
  try {
    const d = parse(raw, "yyyy-MM-dd", new Date());
    if (isValid(d)) return format(d, "MMM d, yyyy");
  } catch {}
  return raw;
}

// Report dates arrive as yyyy-MM-dd or M/d/yyyy; show them as "Sep 17, 2026".
// Anything else (times, amounts, names) is returned untouched.
const REPORT_DATE_FORMATS = [
  [/^\d{4}-\d{2}-\d{2}$/, "yyyy-MM-dd"],
  [/^\d{1,2}\/\d{1,2}\/\d{4}$/, "M/d/yyyy"],
];

export function formatReportDate(value) {
  const [, pattern] =
    REPORT_DATE_FORMATS.find(([regex]) => regex.test(String(value))) ?? [];
  const date = pattern ? parse(value, pattern, new Date()) : null;

  return date && isValid(date) ? format(date, "MMM d, yyyy") : value;
}

export function parseFlexibleDate(raw) {
  if (!raw) return null;

  if (raw instanceof Date && isValid(raw)) return raw;

  const value = String(raw).trim();
  const patterns = ["yyyy-MM-dd", "d MMM yyyy", "MMM d, yyyy", "MMMM d, yyyy"];

  for (const pattern of patterns) {
    try {
      const parsed = parse(value, pattern, new Date());
      if (isValid(parsed)) return parsed;
    } catch {}
  }

  const fallback = new Date(value);
  return isValid(fallback) ? fallback : null;
}

export function formatFlexibleDate(raw, fallback = "—") {
  const date = parseFlexibleDate(raw);
  return date ? format(date, "MMM d, yyyy") : fallback;
}

export function getDaysLeft(raw) {
  const date = parseFlexibleDate(raw);
  if (!date) return null;

  return differenceInCalendarDays(startOfDay(date), startOfDay(new Date()));
}

export function getDaysLeftLabel(raw, { includeToday = true } = {}) {
  const daysLeft = getDaysLeft(raw);

  if (daysLeft === null) return null;
  if (daysLeft < 0) return "Ended";
  if (daysLeft === 0) return includeToday ? "Ends today" : "0 days left";
  if (daysLeft === 1) return "1 day left";

  return `${daysLeft} days left`;
}

export const preferredTime = (startTime, endTime) => {
  let preferredTime =
    startTime && endTime
      ? `${formatTime(startTime)} – ${formatTime(endTime)}`
      : null;

  return preferredTime;
};

export function getMinimumUserSlot(selectedPlan) {
  if (selectedPlan?.price == "0.00") {
    return Math.max(selectedPlan?.used_users, 2);
  } else {
    return Math.max(selectedPlan?.used_users, 1);
  }
}

export const StatusBadge = ({ status, className, icon: Icon }) => {
  if (!status) return status;

  const statusKey = status
    ?.toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");

  const displayStatus =
    statusKey === "un_confirmed" ? "Unconfirmed" : status.replace(/_/g, " ");

  const commonStatusClasses = {
    gray: "bg-neutral-100 text-neutral-500 text-xs border border-neutral-300",
    red: "bg-red-100 text-red-600 text-xs border border-red-300",
    orange: "bg-[#FF5722]/15 text-[#D84315] text-xs border border-[#FF5722]/60",
    yellow: "bg-yellow-100 text-yellow-600 text-xs border border-yellow-300",
    indigo: "bg-indigo-100 text-indigo-600 text-xs border border-indigo-300",
    sky: "bg-sky-100 text-sky-600 text-xs border border-sky-300",
    green: "bg-green-100 text-green-600 text-xs border border-green-300",
    blue: "bg-blue-50 text-blue-700 text-xs border border-blue-200",
    purple: "bg-purple-100 text-purple-600 text-xs border border-purple-300",
    pink: "bg-pink-100 text-pink-600 text-xs border border-pink-300",
  };

  const statusClasses = {
    refunded: commonStatusClasses.red,
    available: commonStatusClasses.green,
    in_progress: commonStatusClasses.pink,
    completed: commonStatusClasses.green,
    active: commonStatusClasses.green,
    archived: commonStatusClasses.gray,
    inactive: commonStatusClasses.gray,
    unlisted: commonStatusClasses.gray,
    cancelled: commonStatusClasses.red,
    noshow: commonStatusClasses.orange,
    no_show: commonStatusClasses.orange,
    draft: commonStatusClasses.gray,
    open: commonStatusClasses.yellow,
    closed: commonStatusClasses.green,
    paid: commonStatusClasses.green,
    free: commonStatusClasses.indigo,
    approved: commonStatusClasses.green,
    pending: commonStatusClasses.yellow,
    pending_appointment: commonStatusClasses.yellow,
    un_confirmed: commonStatusClasses.yellow,
    waiting: commonStatusClasses.purple,
    converted: commonStatusClasses.green,
    confirmed: commonStatusClasses.blue,
    listed: commonStatusClasses.green,
    awaiting_payment: commonStatusClasses.yellow,
    past_due: commonStatusClasses.red,
    unpaid: commonStatusClasses.red,
    required: commonStatusClasses.red,
    expired: commonStatusClasses.gray,
    used: commonStatusClasses.gray,
    // Email / text message delivery (Messages history, campaigns).
    sent: commonStatusClasses.green,
    accepted: commonStatusClasses.green,
    delivered: commonStatusClasses.green,
    opened: commonStatusClasses.green,
    clicked: commonStatusClasses.green,
    queued: commonStatusClasses.yellow,
    deferred: commonStatusClasses.yellow,
    failed: commonStatusClasses.red,
    rejected: commonStatusClasses.red,
    bounced: commonStatusClasses.red,
    complained: commonStatusClasses.red,
    undelivered: commonStatusClasses.red,
    suppression: commonStatusClasses.red,
    suppressed: commonStatusClasses.red,
    dropped: commonStatusClasses.red,
    skipped: commonStatusClasses.gray,
  };

  return (
    <Badge
      className={cn(
        "justify-center capitalize",
        statusClasses[statusKey] ||
          "border border-neutral-400 bg-neutral-200 text-neutral-700",
        className,
      )}
    >
      {Icon && <Icon />}
      {displayStatus}
    </Badge>
  );
};

export const cardLogos = {
  visa: "data:image/png;base64,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",
  mastercard:
    "https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg",
  amex: "https://upload.wikimedia.org/wikipedia/commons/3/30/American_Express_logo.svg",
  discover:
    "https://upload.wikimedia.org/wikipedia/commons/5/50/Discover_Card_logo.svg",
};

// day list
export function generateFrequencyOptions(type) {
  const options = [];

  if (type === "DAILY") {
    for (let i = 1; i <= 30; i++) {
      options.push({
        label: i === 1 ? "1 day" : `${i} days`,
        value: String(i),
      });
    }
  }

  if (type === "WEEKLY") {
    for (let i = 1; i <= 20; i++) {
      options.push({
        label: i === 1 ? "1 week" : `${i} weeks`,
        value: String(i),
      });
    }
  }

  if (type === "MONTHLY") {
    for (let i = 1; i <= 12; i++) {
      options.push({
        label: i === 1 ? "1 month" : `${i} months`,
        value: String(i),
      });
    }
  }

  return options;
}

export const getProviderId = (provider) => {
  return (
    provider?.id ??
    provider?.value ??
    provider?.user_id ??
    provider?.user?.id ??
    provider?.user?.value ??
    provider?.pivot?.user_id
  );
};

export const getProviderValue = (provider) => {
  const providerId = getProviderId(provider);

  if (providerId === null || providerId === undefined || providerId === "") {
    return "";
  }

  const value = String(providerId);

  return value === "undefined" || value === "null" ? "" : value;
};

export const getDefaultProviderValue = (providers = []) =>
  getProviderValue(providers[0]);

export const getItemProviderUsers = (item, fallbackUsers = []) =>
  item?.type === "services"
    ? item?.users?.length
      ? item.users
      : fallbackUsers
    : item?.users || fallbackUsers;

export const normalizeItemsProviderIds = (items = [], fallbackUsers = []) => {
  let hasUpdates = false;

  const normalizedItems = items.map((item) => {
    const currentProviderValue =
      item?.user_id !== null &&
      item?.user_id !== undefined &&
      item?.user_id !== ""
        ? String(item.user_id)
        : "";

    const providerValue =
      currentProviderValue ||
      getProviderValue(item?.user) ||
      getDefaultProviderValue(getItemProviderUsers(item, fallbackUsers));

    if (providerValue === currentProviderValue) {
      return item;
    }

    hasUpdates = true;
    return { ...item, user_id: providerValue };
  });

  return { hasUpdates, items: normalizedItems };
};

// Sort service options by their saved `order` value so UI always shows the intended sequence.
export const sortByOrder = (items = []) => {
  return [...items].sort((a, b) => {
    const aOrder = Number.isFinite(Number(a?.order))
      ? Number(a.order)
      : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(Number(b?.order))
      ? Number(b.order)
      : Number.MAX_SAFE_INTEGER;

    return aOrder - bOrder;
  });
};

// 30 days either side of now in the location timezone (browser clock when no
// timezone is given).
export const createDefaultFilterDateRange = (timezone) => {
  const now = getNowInTimezone(timezone).getTime();

  return {
    from: new Date(now - 30 * 24 * 60 * 60 * 1000),
    to: new Date(now + 30 * 24 * 60 * 60 * 1000),
  };
};

export const formatDateKey = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? format(date, "yyyy-MM-dd")
    : "";

export const isDateRangeChangedFromDefault = (
  dateRange,
  defaultDateRange = createDefaultFilterDateRange(),
) =>
  formatDateKey(dateRange?.from) !== formatDateKey(defaultDateRange.from) ||
  formatDateKey(dateRange?.to) !== formatDateKey(defaultDateRange.to);

// Date-only strings ("2026-07-16") are parsed by `new Date()` as UTC midnight,
// which renders as the previous day in timezones behind UTC. Parse them as
// local calendar dates instead; Date instances and datetime strings pass through.
export function parseLocalDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());

  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  const parsedDate = new Date(value);
  return isValid(parsedDate) ? parsedDate : null;
}

export function formatLocalDate(value) {
  const date = parseLocalDate(value);
  return date ? format(date, "yyyy-MM-dd") : "";
}
